import React, { CSSProperties, useMemo } from 'react';
import clsx from 'clsx';

import { formatImage } from '../../utils/image';

import {
  Hero as StyledHero,
  ImageWrapper,
  ImagePlaceholder,
  ImageLink,
  Content,
  Heading,
  Description,
  ActionList,
  ActionItem,
  Countdown,
  Products,
  ProductsFooter,
} from './wrappers';

import Floater, { IFloater } from './Floater';
import { FloaterImage, FloaterLink, FloaterPreHeading, FloaterHeading } from './Floater/wrappers';
import Badge from '../Badge';
import { IPicture, Picture } from '../Image';
import { ProductGrid, ProductGridItem } from '../ProductGrid';
import Button from '../Button';
import ProductCard, { IProductCard } from '../ProductCard';
import Container from '../Container';
import ProductPrices from '../../components-legacy/ProductPrices';

interface IHeroLink {
  text: string;
  url: string;
  internal?: boolean;
}

interface IHero {
  buttonStyle?: CSSProperties;
  content?: { html: string; markdown: string };
  contentPosition?: 'Left' | 'Center' | 'Right';
  contentStyle?: CSSProperties;
  floaters?: IFloater[];
  heading: string;
  headingItalic?: boolean;
  headingSize?: 'Small' | 'Normal' | 'Medium' | 'Large' | 'ExtraLarge';
  heroLink?: IHeroLink;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  imagePlaceholderRatio?: string;
  imageQuality?: number;
  inPanel?: boolean;
  imageLoading?: 'lazy' | 'eager';
  links: IHeroLink[];
  mobileImage?: string;
  products?: IProductCard[];
  productsLink?: {
    path: string;
    title: string;
  };
  renderActionLink?: (link: IHeroLink, style?: CSSProperties) => JSX.Element;
  renderCountdown?: () => JSX.Element;
  renderFloaterLink?: (floater: IFloater, children: React.ReactNode) => JSX.Element;
  renderHeroLink?: (link: IHeroLink) => JSX.Element;
  titleHeadingLevel?: number;
  width: 'Wide' | 'Narrow';
}

const defaultActionLinkRenderer = (link: IHeroLink, style?: CSSProperties) => (
  <Button href={link.url} as="a" title={link.text} tone="soft" size="small" style={style}>
    {link.text}
  </Button>
);

const defaultHeroLinkRenderer = (link: IHeroLink) => (
  <ImageLink href={link.url} title={link.text} />
);

const defaultFloaterLinkRenderer = (floater: IFloater, children: React.ReactNode) => (
  <FloaterLink href={floater.link} title={floater.heading}>
    {children}
  </FloaterLink>
);

const Hero = ({
  buttonStyle,
  content,
  contentPosition,
  contentStyle,
  floaters,
  heading,
  headingItalic,
  headingSize,
  heroLink,
  image,
  imageHeight,
  imagePlaceholderRatio,
  imageQuality,
  imageWidth,
  inPanel,
  imageLoading,
  links,
  mobileImage,
  products,
  productsLink,
  renderActionLink = defaultActionLinkRenderer,
  renderCountdown,
  renderFloaterLink = defaultFloaterLinkRenderer,
  renderHeroLink = defaultHeroLinkRenderer,
  titleHeadingLevel = 2,
  width,
}: IHero) => {
  // Calculate image laceholder ratio

  let placeholderRatio = undefined;
  if (imagePlaceholderRatio) {
    placeholderRatio =
      (parseInt(imagePlaceholderRatio.split(':')[1], 10) /
        parseInt(imagePlaceholderRatio.split(':')[0], 10)) *
      100;
  }

  const imageProps = useMemo(() => {
    if (!image) return false;

    const quality = imageQuality && imageQuality > 0 ? imageQuality : undefined;

    return {
      image: {
        src: formatImage(image, { width: 1920 }),
        srcSet: `
          ${formatImage(image, { width: 630, quality })} 630w,
          ${formatImage(image, { width: 840, quality })} 840w,
          ${formatImage(image, { width: 1344, quality })} 1344w
        `,
        sizes:
          contentPosition === 'Center'
            ? '(min-width: 1344px) 1920px, 100vw'
            : '(min-width: 900px) 70vw, (min-width: 1920px) 1344px, 100vw',
        height: imageHeight,
        width: imageWidth,
      },
      sources: [
        {
          type: 'image/webp',
          media: '(min-width: 900px)',
          srcSet: `
            ${formatImage(image, { width: 630, output: 'webp', quality })} 630w,
            ${formatImage(image, { width: 840, output: 'webp', quality })} 840w,
            ${formatImage(image, { width: 1344, output: 'webp', quality })} 1344w,
            ${formatImage(image, { width: 1920, output: 'webp', quality })} 1920w
          `,
          sizes:
            contentPosition === 'Center'
              ? '(min-width: 1920px) 1344px, 100vw'
              : '(min-width: 900px) 70vw, (min-width: 1920px) 1344px, 100vw',
        },
        {
          type: 'image/webp',
          media: '(max-width: 899px)',
          srcSet: `
            ${formatImage(mobileImage || image, { width: 320, output: 'webp', quality })} 320w,
            ${formatImage(mobileImage || image, { width: 420, output: 'webp', quality })} 420w,
            ${formatImage(mobileImage || image, { width: 768, output: 'webp', quality })} 768w,
            ${formatImage(mobileImage || image, { width: 900, output: 'webp', quality })} 900w,
          `,
          sizes:
            '(max-width: 320px) 320px), (max-width: 420px) 420px), (max-width: 768px) 768px), (max-width: 900px) 900px)',
        },
      ],
    } as IPicture;
  }, [image]);

  return (
    <>
      <StyledHero className={clsx(width?.toLocaleLowerCase(), inPanel && 'in-panel')}>
        <ImageWrapper
          className={clsx(
            `content-pos-${contentPosition?.toLocaleLowerCase()}`,
            inPanel && 'in-panel',
          )}>
          {heroLink && renderHeroLink(heroLink)}
          {imageProps && <Picture {...imageProps} imageLoading={imageLoading} />}
          {!image && <ImagePlaceholder ratio={placeholderRatio} style={contentStyle} />}
          {floaters &&
            floaters.map(
              (floater, index) =>
                (floater.articleNumber || floater.image) && (
                  <Floater
                    posTop={floater.posTop}
                    posLeft={floater.posLeft}
                    posTopMobile={floater.posTopMobile}
                    posLeftMobile={floater.posLeftMobile}
                    textColor={floater.textColor}
                    key={
                      floater.image
                        ? `${floater.image}-${index}`
                        : floater.articleNumber
                          ? `${floater.articleNumber}-${index}`
                          : `floater-${index}`
                    }
                    imageSizeMobile={floater.image && floater.imageSizeMobile}
                    imageSizeDesktop={floater.image && floater.imageSizeDesktop}
                    hasImage={!floater.articleNumber}
                    inPanel={inPanel}>
                    {floater.articleNumber &&
                      renderFloaterLink(
                        floater,
                        <>
                          {floater.badgeLabel && (
                            <Badge
                              label={floater.badgeLabel}
                              tail={floater.badgeTail}
                              style={floater.badgeStyle}
                            />
                          )}
                          <FloaterPreHeading>{floater.preHeading}</FloaterPreHeading>
                          <FloaterHeading>{floater.heading}</FloaterHeading>
                          {floater.price && (
                            <ProductPrices
                              size="large"
                              priceObject={{
                                sellingPrice: floater.price.price,
                                referencePrice: floater.price.referencePrice,
                                originalPrice: floater.price.originalPrice,
                                pilPrice: floater.price.pilPrice,
                                componentPrice: floater.price.componentPrice,
                              }}
                            />
                          )}
                        </>,
                      )}
                    {!floater.articleNumber &&
                      floater.link &&
                      renderFloaterLink(
                        floater,
                        <FloaterImage src={floater.image} alt="kampanj" />,
                      )}
                    {!floater.articleNumber && !floater.link && (
                      <FloaterImage src={floater.image} alt="kampanj" />
                    )}
                  </Floater>
                ),
            )}
          {!!renderCountdown && (
            <>
              <Countdown>{renderCountdown()}</Countdown>
            </>
          )}
        </ImageWrapper>
        {(heading || !!content?.html || !!content?.markdown) && (
          <Content
            className={clsx(
              contentPosition === 'Left' && 'pos-left',
              contentPosition === 'Center' && 'pos-center',
            )}
            style={contentStyle}>
            <Heading
              as={titleHeadingLevel === 1 ? 'h1' : 'h2'}
              className={clsx(
                headingSize === 'Small' && 'font-size-small',
                headingSize === 'Large' && 'font-size-large',
                headingSize === 'ExtraLarge' && 'font-size-extralarge',
                headingItalic && 'italic',
              )}>
              {heading}
            </Heading>
            {content && <Description dangerouslySetInnerHTML={{ __html: content }} />}
            {links && links.length > 0 && (
              <ActionList>
                {links.map((link, index) => (
                  <ActionItem key={`${link.url}-${index}`}>
                    {renderActionLink(link, buttonStyle)}
                  </ActionItem>
                ))}
              </ActionList>
            )}
          </Content>
        )}
      </StyledHero>

      {products && products.length > 0 && (
        <Products>
          <Container>
            <ProductGrid
              centerItems={products.length < 4}
              overflowScroll="always"
              onClickPrev={() => console.log('Prev')}
              onClickNext={() => console.log('Next')}
              productView="grid-small">
              {products?.map((product, index) => (
                <ProductGridItem key={`{${product.title}-${index}`}>
                  <ProductCard {...product} />
                </ProductGridItem>
              ))}
            </ProductGrid>
            {productsLink && (
              <ProductsFooter>
                <Button as="a" href={productsLink.path} title={productsLink.title} tone="ghost">
                  {productsLink.title}
                </Button>
              </ProductsFooter>
            )}
          </Container>
        </Products>
      )}
    </>
  );
};

export default Hero;
